import { initializeApp, type FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getAuth, onAuthStateChanged, type Auth, type User } from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import { getFunctions, type Functions } from 'firebase/functions';

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;
let storage: FirebaseStorage | null = null;
let functions: Functions | null = null;
let userPromise: Promise<User> | null = null;

const region =
  (import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION as string | undefined) ||
  'europe-west1';

export function initFirebase(): FirebaseApp {
  if (app) return app;

  const config = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };

  // When values are missing (e.g. first-run before .env.local is filled),
  // fall back so `initializeApp` still receives a minimal object; replace
  // with real keys from the Firebase console for a working app.
  if (!config.projectId) {
    config.projectId = 'moments-app';
    config.apiKey = config.apiKey ?? 'replace-me';
    config.appId = config.appId ?? 'replace-me';
  }

  app = initializeApp(config);

  // App Check + Google reCAPTCHA **v3 site key** (public). The matching **secret**
  // is configured only in Firebase Console → App Check — never in VITE_* or client code.
  const appCheckSiteKey = (
    import.meta.env.VITE_FIREBASE_APPCHECK_RECAPTCHA_SITE_KEY as string | undefined
  )?.trim();
  const appCheckDebug = (
    import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN as string | undefined
  )?.trim();
  if (typeof window !== 'undefined' && appCheckDebug) {
    const g = globalThis as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean };
    g.FIREBASE_APPCHECK_DEBUG_TOKEN =
      appCheckDebug === '1' || appCheckDebug.toLowerCase() === 'true' ? true : appCheckDebug;
  }
  if (typeof window !== 'undefined' && appCheckSiteKey) {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  }

  auth = getAuth(app);
  auth.languageCode = 'he';
  db = getFirestore(app);
  storage = getStorage(app);
  functions = getFunctions(app, region);

  // Resolves on first successful sign-in (e.g. phone). No automatic anonymous session.
  userPromise = new Promise<User>((resolve) => {
    const off = onAuthStateChanged(auth!, (user) => {
      if (user) {
        off();
        resolve(user);
      }
    });
  });

  return app;
}

export function getApp(): FirebaseApp {
  if (!app) throw new Error('Firebase not initialised yet');
  return app;
}

export function getAuthOrThrow(): Auth {
  if (!auth) throw new Error('Firebase not initialised yet');
  return auth;
}

export function getDb(): Firestore {
  if (!db) throw new Error('Firebase not initialised yet');
  return db;
}

export function getStorageOrThrow(): FirebaseStorage {
  if (!storage) throw new Error('Firebase not initialised yet');
  return storage;
}

export function getFunctionsOrThrow(): Functions {
  if (!functions) throw new Error('Firebase not initialised yet');
  return functions;
}

function waitForSignedInUser(a: Auth): Promise<User> {
  if (a.currentUser) return Promise.resolve(a.currentUser);
  return new Promise((resolve, reject) => {
    let unsub: () => void;
    const tid = window.setTimeout(() => {
      unsub();
      reject(new Error('פג הזמן בהמתנה לאימות משתמש'));
    }, 60_000);
    unsub = onAuthStateChanged(a, (user) => {
      if (user) {
        window.clearTimeout(tid);
        unsub();
        resolve(user);
      }
    });
  });
}

/**
 * Returns the current signed-in user, or waits until the user completes sign-in
 * (e.g. phone OTP). After sign-out, waits for the next sign-in.
 */
export function ensureUser(): Promise<User> {
  const a = auth;
  if (!a) throw new Error('Firebase not initialised yet');
  if (a.currentUser) return Promise.resolve(a.currentUser);
  if (!userPromise) throw new Error('Firebase not initialised yet');
  return userPromise.then(() => waitForSignedInUser(a));
}
